// Friendly error screen when Firebase config is missing
import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { AlertCircle, Copy, Check, ExternalLink } from 'lucide-react';
import AppLogo from './AppLogo';
import { copyTextToClipboard } from '../lib/clipboard';

interface FirebaseConfigErrorProps {
  missing: string[];
  message?: string;
}

export default function FirebaseConfigError({ missing, message }: FirebaseConfigErrorProps) {
  const [copied, setCopied] = useState(false);

  const envVarsText = missing.map(key => `${key}=your_value_here`).join('\n');

  // Rendered by AppErrorBoundary, outside the ToasterProvider, so no toast here: a failed copy is said
  // inline and the template below is already selectable text.
  const [copyFailed, setCopyFailed] = useState(false);
  const handleCopy = async () => {
    const ok = !!(await copyTextToClipboard(envVarsText));
    setCopyFailed(!ok);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--surface-muted)] flex items-center justify-center p-8">
      <Card className="max-w-2xl w-full p-8">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
            <AlertCircle className="w-6 h-6 text-red-600" />
          </div>
          <div>
            <AppLogo className="h-8 w-auto" clickable={false} />
          </div>
        </div>

        <h1 className="text-2xl font-bold text-[var(--text-primary)] mb-4">
          Configuration Error
        </h1>

        <p className="text-[var(--text-secondary)] mb-6">
          The application is missing required Firebase configuration. Please add the missing environment variables to continue.
        </p>

        {message && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-yellow-800 font-mono break-all">{message}</p>
          </div>
        )}

        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold text-red-900">
              Missing Environment Variables:
            </h2>
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopy}
              className="h-7 px-2 text-xs"
            >
              {copied ? (
                <>
                  <Check className="w-3 h-3 mr-1" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="w-3 h-3 mr-1" />
                  Copy
                </>
              )}
            </Button>
          </div>
          {copyFailed && (
            <p className="mb-2 text-xs text-red-800" role="status" data-testid="config-copy-failed">
              Copy didn't work in this browser. Select the template under "How to Fix" and copy it.
            </p>
          )}
          <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
            {missing.map((key) => (
              <li key={key} className="font-mono">{key}</li>
            ))}
          </ul>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h3 className="text-sm font-semibold text-blue-900 mb-2">
            How to Fix:
          </h3>
          <ol className="list-decimal list-inside text-sm text-blue-700 space-y-2 mb-4">
            <li>Create or edit <code className="bg-blue-100 px-1 rounded">.env</code> file in the project root</li>
            <li>Add the missing variables (click Copy above to get a template)</li>
            <li>Get your Firebase config from <a href="https://console.firebase.google.com" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">Firebase Console <ExternalLink className="w-3 h-3" /></a></li>
            <li>Restart the development server: <code className="bg-blue-100 px-1 rounded">npm run dev</code></li>
            <li>For production: Set environment variables in your hosting platform (Firebase Hosting, Vercel, etc.)</li>
          </ol>
          <div className="mt-4 p-3 bg-[var(--surface-card)] rounded border border-blue-200">
            <p className="text-xs font-mono text-[var(--text-secondary)] whitespace-pre-wrap break-all select-all">
              {envVarsText}
            </p>
          </div>
        </div>

        <div className="flex gap-3">
          <Button
            onClick={() => window.location.reload()}
            variant="outline"
            className="flex-1"
          >
            Reload Page
          </Button>
          <Button
            onClick={() => window.location.href = '/'}
            className="flex-1 bg-rose-600 hover:bg-rose-700 text-white"
          >
            Go to Home
          </Button>
        </div>
      </Card>
    </div>
  );
}
